import React from 'react'
import PhoneNumber, { Country } from 'react-phone-number-input'
import PhoneInput from 'react-phone-number-input/input'
import FormControl, { useFormControl } from '@mui/material/FormControl'
import FormHelperText from '@mui/material/FormHelperText'
import InputLabel from '@mui/material/InputLabel'
import Stack from '@mui/material/Stack'
import { alpha, styled } from '@mui/material/styles'

import colors from '@/assets/scss/_themes-vars.module.scss'
import { useLanguage } from '@/hooks/useLanguage'
import 'react-phone-number-input/style.css'
import { getCountryCode } from '@/utils/countryCode'

export type PhoneNumberInputProps = {
  /**
   * The defaultCountry must be a supported [country code](https://en.wikipedia.org/wiki/ISO_3166-1_alpha-2)
   * @default 'FR' for non-English languages, 'US' for English ('en' or 'en-US')
   */
  defaultCountry?: Country
  /**
   * If `true`, the component is disabled.
   * @default false
   */
  disabled?: boolean
  /**
   * If `true`, the component is displayed in an error state.
   * @default false
   */
  error?: boolean
  /**
   * The helper text content.
   */
  helperText?: React.ReactNode
  /**
   * The label content.
   */
  label?: string
  /**
   * Name attribute of the `input` element.
   */
  name?: string
  /**
   * Callback fired when the input is blur.
   *
   * @param {object} event The event source of the callback.
   */
  onBlur?: (event: React.FocusEvent) => void
  /**
   * Callback fired when the value is changed.
   *
   * @param {string} value
   */
  onChange: (value?: string) => void
  /**
   * Callback fired when the country code is changed.
   *
   * @param {Country} countryCode
   */
  onCountryChange?: (countryCode?: Country) => void
  /**
   * It prevents the user from changing the value of the field
   * (not from interacting with the field).
   * @default false
   */
  readOnly?: boolean
  /**
   * Phone number value
   */
  value?: string
}

const StyledInput = styled('input')({
  font: 'inherit',
  letterSpacing: 'inherit',
  color: 'currentColor',
  boxSizing: 'content-box',
  background: '#fff',
  margin: 0,
  display: 'block',
  minWidth: 0,
  width: '100%',
  animationName: 'mui-auto-fill-cancel',
  animationDuration: '10ms',
  fontSize: '14px',
  padding: '8px 12px 8px 0px',
  height: 'auto',
  border: 'none',
  borderRadius: '4px',
  '&:focus-visible': {
    outline: 0,
    boxShadow: 'none',
  },
})

function PhoneNumberWrapper(props: PhoneNumberInputProps) {
  const language = useLanguage()
  const { defaultCountry = getCountryCode(language), disabled, error, label, name, onBlur, onChange, onCountryChange, readOnly, value } = props
  const { focused, onBlur: onFormControlBlur, onFocus } = useFormControl() || {}

  const inputProps = {
    'aria-label': label ?? 'phone number',
    disabled,
    inputComponent: StyledInput,
    name,
    onBlur: (e: React.FocusEvent<HTMLDivElement>) => {
      onBlur?.(e)
      onFormControlBlur?.(e)
    },
    onChange,
    onFocus,
    readOnly,
    value,
  }

  return (
    <Stack
      direction="row"
      alignItems="center"
      sx={[
        {
          backgroundColor: (theme) => theme.palette.common.white,
          lineHeight: '1.25rem',
          boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.05), 0px 0px 0px 2px #FFFFFF',
          border: `1px solid ${colors.gray300}`,
          borderRadius: '4px',
          pl: '12px',
          '& .PhoneInput': {
            flex: 1,
          },
        },
        !!error && { borderColor: (theme) => theme.palette.error.main },
        !!focused && { borderColor: alpha(colors.primaryMain, 0.38) + '!important' },
        !!label && { mt: '24px' },
      ]}
    >
      {readOnly ? (
        // @ts-ignore
        <PhoneInput {...inputProps} country={defaultCountry} />
      ) : (
        <PhoneNumber
          {...inputProps}
          countryCallingCodeEditable={false}
          countrySelectProps={{ unicodeFlags: true }}
          defaultCountry={defaultCountry}
          international
          onCountryChange={onCountryChange}
        />
      )}
    </Stack>
  )
}

export default function PhoneNumberInput(props: PhoneNumberInputProps) {
  const { error, helperText, label } = props

  return (
    <FormControl error={error} fullWidth>
      {label && (
        <InputLabel
          shrink
          sx={{
            color: colors.gray700,
            fontSize: '0.875rem',
            fontWeight: 500,
            lineHeight: '1.25rem',
            transform: 'none',
            '&.Mui-focused:not(.Mui-error)': {
              color: colors.gray700,
            },
          }}
        >
          {label}
        </InputLabel>
      )}
      <PhoneNumberWrapper {...props} />
      {helperText && <FormHelperText>{helperText}</FormHelperText>}
    </FormControl>
  )
}
